import React from 'react';
import { Badge, Box, Card, Stack, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ReactComponent as FilterAltOutlinedIcon } from '../../../static/icons/filter.svg';
import { ReactComponent as TableIcon } from '../../../static/icons/table.svg';
import { ReactComponent as GraphIcon } from '../../../static/icons/graph.svg';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import { useRef, useState } from 'react';
import FilterDrawer from '../../FilterDrawer/FilterDrawer';
import WidgetDownload from '../../Widget-Download/Widget-Download';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { getLiveCalls } from '../../../services/api-service';
import { WIDGET_REFRESH_INTERVAL } from '../../../utils/constants';
import useChartResize from '../../../hooks/useChartResize';
import { getLastFetchedDetailFromBrowser } from '../../../services/application-service';
import OZTable from '../../OZTable/OZTable';

const useStyles: any = makeStyles((theme: Theme) => ({
	typeButton: {
		padding: '5px !important'
	},
	filterItem: {
		fontSize: 12,
		height: 24,
		borderRadius: 20,
		background: 'rgba(0, 142, 255, 0.1)',
		border: '1px solid #008eff',
		padding: `0 ${theme.spacing(2)}`,
		display: 'flex',
		alignItems: 'center'
	},
	itemIndicator: {
		width: 5,
		height: 5,
		borderRadius: 1,
		display: 'inline-block',
		position: 'relative',
		top: '-2px',
		marginRight: 4
	},
	filterItemText: {
		paddingTop: 8,
		color: '#536580'
	},
	filterItemSubText: {
		color: '#99A0A8'
	}
}));

const TableColumns = [
	{ name: 'Campaign Name', key: 'campaignName', align: 'left' },
	{ name: 'Calls', key: 'pacing', align: 'left' }
];


function Pacing(props: any) {
	const classes = useStyles();
	const downloadContent = useRef<null | any>(null);
	const [isDrawerOpen, setIsDrawerOpen] = useState<boolean | undefined>(false);
	const [pacingFilter, setPacingFilter] = useState<any>(props?.selectedFilters?.filters || []);
	const [graphData, setGraphData] = useState<any>([]);
	const [showGraphData, setShowGraphData] = useState<Boolean>(true);
	const [filterType, setFilterType] = useState<any>(props?.selectedFilters?.filterSection?.id || '0');
	const [filterSection, setFilterSection] = useState<any>(props?.selectedFilters?.filterSection || {});
	const [responseLength, setresponseLength] = useState<Number>(0);
	const [campaignName, setCampaignName] = useState<any>([]);
	const [lastFetchedTime, setLastFetchedTime] = useState<any>('');
	const [rowData, setRowData] = useState<Array<any>>([]);
	const [columnData] = useState<Array<any>>(TableColumns);
	const { chartContainerRef, chartDimensions, chartElementRef, } = useChartResize();

	const intervalRef = useRef<any>(null);
	const pacingLoadingRef = useRef<boolean>(false);

	const handleDownload = (type: string) => {
		console.log(type);
	};

	const toggleDrawer = (isOpen: boolean) => {
		setIsDrawerOpen(isOpen);
	};

	const clearFilter = () => {
		setPacingFilter([]);
		props?.handleFilterChange?.([], filterSection, props?.widgetId);
	};

	const handleFilterDelete = (id: Number) => {
		const newFilter = pacingFilter.filter((item: any) => item.id !== id);
		setPacingFilter(newFilter);
		props?.handleFilterChange?.(newFilter, filterSection, props?.widgetId);
	};

	const filterItems = pacingFilter.map((item: any, index: number) => (
		<div className={classes.filterItem}>
			<div style={{ marginBottom: "8px" }}>
				<p className={classes.filterItemText}>
					<span className={classes.itemIndicator} style={{ background: item.color }}></span>
					{item.label}
				</p>
				<b>{item.target}</b>
			</div>
			<CloseOutlinedIcon onClick={() => handleFilterDelete(item.id)} color="primary" style={{ cursor: 'pointer', fontSize: '14px', marginLeft: "4px" }} />
		</div>
	));

	const handleFilterExistingData = (filteredData: any) => {
		setPacingFilter(filteredData);
		props?.handleFilterChange?.(filteredData, filterSection, props?.widgetId);
	};

	const handleApplyFilters = ({ selectedFilters, filterType: newFilterType, filterSection: newfilterSection }: any) => {
		setPacingFilter(selectedFilters);
		setFilterType(newFilterType);
		setFilterSection(newfilterSection);
		props?.handleFilterChange?.(selectedFilters, newfilterSection, props?.widgetId);
		toggleDrawer(false);
	};

	const handleViewToggle = () => {
		setShowGraphData(prev => !prev);
	};

	const retrieveGraphData = async (filters: any) => {
		try {
			if (pacingLoadingRef.current) {
				console.log('Widget data fetch already in progress, skipping this cycle pacingLoading');
				return;
			}
			pacingLoadingRef.current = true;
			const apiResponse: any[] = await getLiveCalls({ campaignId: filters, fetchType: 'pacing' });
			try {
				const sortedResponse = [...apiResponse];
				sortedResponse.sort((a: any, b: any) => +b.pacing - +a.pacing);
				setRowData(sortedResponse);
			} catch (error) {
				console.error('Error fetching pacing:', error);
			}
			const callsData = apiResponse.map((item: any) => ({
				y: +item.pacing,
				...item
			}));
			const campaignName = apiResponse.map((item: any) => item.campaignName);
			setCampaignName(campaignName);
			const graphOptions = [
				{
					name: 'Calls',
					color: '#008DFF',
					data: callsData
				}
			];

			setGraphData(graphOptions);
			setLastFetchedTime(getLastFetchedDetailFromBrowser());
		}
		catch (error) {
			console.error('Error fetching pacing:', error);
		} finally {
			pacingLoadingRef.current = false;
		}
	};

	const options = {
		chart: {
			type: 'column',
			height: chartDimensions.height,
		},
		credits: false,
		title: {
			text: ''
		},
		xAxis: {
			title: {
				text: 'Campaigns ➡',
				style: {
					fontWeight: 'bold'
				}
			},
			categories: campaignName,
			crosshair: true,
			labels: {
				rotation: -45,
				style: {
					fontSize: '12px',
					fontFamily: 'Roboto'
				}
			}
		},
		yAxis: {
			title: {
				text: 'Calls',
				style: { fontWeight: 'bold' }
			},
			gridLineDashStyle: 'dash'
		},
		legend: {
			enabled: false
		},
		tooltip: {
			shared: true
		},
		plotOptions: {
			series: {
				cursor: 'pointer',
				pointWidth: 10,
				borderRadius: 2,
				groupPadding: 0.3,
				dataLabels: {
					enabled: true,
				}
			}
		},
		series: graphData
	};

	React.useEffect(() => {
		const filterIds = (pacingFilter.length && !(responseLength === pacingFilter.length)) ? pacingFilter.map((item: any) => item.id).join(',') : '0';
		// const filterIds = pacingFilter.length ? pacingFilter.map((item: any) => item.id).join(',') : '0';
		retrieveGraphData(filterIds);
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		intervalRef.current = setInterval(() => retrieveGraphData(filterIds), WIDGET_REFRESH_INTERVAL.PACING);
		return () => {
			clearInterval(intervalRef.current);
		};
	}, [pacingFilter]);

	return (
		<>
			<Card ref={downloadContent} className="customCard">
				<div className="card-header">
					<div className="card-title">
						Pacing<span className="card-sub-header">(Predictive)</span>
						<span className="card-title-badge">Live</span>
						<span className="last-fetched-time" style={{ marginLeft: '6px' }}>Last updated at {lastFetchedTime} </span>
					</div>
					<div className="card-actions">
						<div className="card-icon" onClick={() => handleViewToggle()}>
							{showGraphData ? <TableIcon /> : <GraphIcon />}
						</div>
						<WidgetDownload xlsx={showGraphData ? false : true} rowData={(rowData)} columns={columnData} element={downloadContent} handleDownload={handleDownload} name={'Pacing'}></WidgetDownload>
						<div className="card-icon" onClick={() => toggleDrawer(true)}>
							<Badge badgeContent={pacingFilter.length} color="primary">
								<FilterAltOutlinedIcon fontSize="small" style={{ color: '#536580' }} />
							</Badge>
						</div>
					</div>
				</div>
				<Box sx={{ height: "100%", backgroundColor: 'white', }}>
					<Box sx={{ p: 2, pb: 0 }}>
						{pacingFilter.length > 0 && (
							<div className="filter-outer">
								<div className="filter-container">
									<Stack direction={'row'} spacing={1}>
										{filterItems}
									</Stack>
								</div>
								<div className="filter-remove" onClick={clearFilter}>
									Remove
								</div>
							</div>
						)}
					</Box>
					<Box ref={chartContainerRef} sx={{ height: pacingFilter.length > 0 ? 'calc(100% - 80px)' : 'calc(100% - 44px)', }}>
						{showGraphData ? (
							<HighchartsReact ref={chartElementRef} highcharts={Highcharts} options={options} />
						) : (
							<OZTable downloadContent={downloadContent} columns={columnData} rows={rowData} noMaxHeight={true} height={chartDimensions.height} />
						)}
					</Box>
				</Box>
			</Card>
			<FilterDrawer
				isOpen={isDrawerOpen}
				toggleDrawer={toggleDrawer}
				applyFilters={handleApplyFilters}
				existingSelectedFilters={pacingFilter}
				allowedFilters={['Campaign']}
				filterType={'Position'}
				additionalApiParams={{ campaignType: 'predictive', CampaignPosition: 'Running' }}
				setResponseLength={setresponseLength}
				existingFilterCategory={filterType}
				applyFilterOnExistingData={handleFilterExistingData}
			/>
		</>
	);
}

export default Pacing;
